import { useMemo, useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useLinkedRoadmap } from "@/features/roadmap/api/useLinkedRoadmap";
import { getProject } from "@/lib/datasource";
import { formatCurrency } from "@/lib/format";
import {
  Check,
  Save,
  AlertCircle,
  Calendar,
  User,
  MessageSquare,
  ChevronRight,
  ChevronDown,
  Target,
  BarChart3,
  CheckCircle2,
  PlayCircle,
  AlertTriangle,
  Circle,
  Filter,
} from "lucide-react";
import { RoadmapSkeleton, statusDot } from "@/helpers/user-roadmap";

type Status = "not_started" | "in_progress" | "done" | "stuck";
type FilterStatus = Status | "all";
type StepItem = Record<string, any>;

interface FilterState {
  status: FilterStatus;
  showOwner: boolean;
  showDueDate: boolean;
  showComments: boolean;
}

const ProgressRing = ({ value, total }: { value: number; total: number }) => {
  const percentage = total === 0 ? 0 : Math.round((value / total) * 100);
  const circumference = 2 * Math.PI * 16;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="relative">
      <svg className="h-12 w-12 -rotate-90 transform">
        <circle
          cx="24"
          cy="24"
          r="16"
          stroke="currentColor"
          strokeWidth="3"
          fill="transparent"
          className="text-slate-200"
        />
        <circle
          cx="24"
          cy="24"
          r="16"
          stroke="currentColor"
          strokeWidth="3"
          fill="transparent"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className="text-brand-600"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-sm font-bold ml-7">{percentage}%</span>
      </div>
    </div>
  );
};

const ChapterCard = ({
  chapter,
  index,
  isActive,
  onClick,
  done,
  total,
}: {
  chapter: any;
  index: number;
  isActive: boolean;
  onClick: () => void;
  done: number;
  total: number;
}) => {
  const title = String(
    chapter.title ?? chapter.name ?? chapter.slug ?? "(untitled)",
  );
  const percentage = total === 0 ? 0 : Math.round((done / total) * 100);

  return (
    <button
      onClick={onClick}
      className={`w-full text-left rounded-xl p-4 transition-all duration-200 border ${
        isActive
          ? "border-brand-300 bg-brand-50 shadow-sm"
          : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
      }`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-start gap-3">
          <div
            className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
              isActive
                ? "bg-brand-100 text-brand-700"
                : "bg-slate-100 text-slate-600"
            }`}
          >
            <span className="font-semibold text-sm">{index + 1}</span>
          </div>
          <div>
            <h3
              className={`font-semibold text-sm leading-tight ${isActive ? "text-brand-800" : "text-slate-800"}`}
            >
              {title}
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              {done} of {total} tasks completed
            </p>
          </div>
        </div>
        <div className="flex-shrink-0">
          <div className="flex items-center gap-1 text-sm font-medium">
            <span className="text-slate-700">{percentage}%</span>
            <ChevronRight
              className={`h-4 w-4 transition-transform ${isActive ? "text-brand-600" : "text-slate-400"}`}
            />
          </div>
        </div>
      </div>

      <div className="mt-3">
        <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-brand-500 rounded-full transition-all duration-300"
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    </button>
  );
};

const TaskCard = ({
  task,
  progress,
  ownerDraft,
  commentDraft,
  onStatusChange,
  onDueDateChange,
  onOwnerChange,
  onCommentChange,
}: {
  task: any;
  progress: any;
  ownerDraft: Record<string, string>;
  commentDraft: Record<string, string>;
  onStatusChange: (id: string, status: Status) => void;
  onDueDateChange: (id: string, date: string | null) => void;
  onOwnerChange: (id: string, value: string) => void;
  onCommentChange: (id: string, value: string) => void;
}) => {
  const id = String(task.id);
  const title = String(
    task.title ?? task.name ?? task.label ?? task.summary ?? "(untitled)",
  );
  const description =
    task.comment ?? task.description ?? task.notes ?? task.text ?? null;
  const currentStatus = (progress?.status ?? "not_started") as Status;
  const isDirty = ownerDraft[id] != null || commentDraft[id] != null;

  const [expanded, setExpanded] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  const statusConfig = {
    not_started: {
      label: "Not Started",
      icon: Circle,
      color: "text-slate-500 bg-slate-100",
    },
    in_progress: {
      label: "In Progress",
      icon: PlayCircle,
      color: "text-blue-500 bg-blue-50",
    },
    done: {
      label: "Done",
      icon: CheckCircle2,
      color: "text-emerald-500 bg-emerald-50",
    },
    stuck: {
      label: "Stuck",
      icon: AlertTriangle,
      color: "text-amber-500 bg-amber-50",
    },
  };

  const { icon: StatusIcon, label: statusLabel } = statusConfig[currentStatus];

  return (
    <div
      className={`bg-white rounded-lg border transition-all duration-200 ${
        isDirty
          ? "border-blue-300 shadow-sm"
          : "border-slate-200 hover:border-slate-300"
      }`}
    >
      <div className="p-3">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="relative flex-shrink-0">
            <button
              onClick={() => setShowStatusDropdown(!showStatusDropdown)}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium border ${statusConfig[currentStatus].color} border-transparent hover:opacity-90`}
            >
              <StatusIcon className="h-3.5 w-3.5" />
              <span>{statusLabel}</span>
              <ChevronDown className="h-3 w-3" />
            </button>

            {showStatusDropdown && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowStatusDropdown(false)}
                />
                <div className="absolute left-0 top-full mt-1 z-20 w-48 rounded-lg border border-slate-200 bg-white shadow-lg py-1">
                  {Object.entries(statusConfig).map(([status, config]) => {
                    const { icon: Icon, label } = config;
                    return (
                      <button
                        key={status}
                        onClick={() => {
                          onStatusChange(id, status as Status);
                          setShowStatusDropdown(false);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-left"
                      >
                        <Icon className="h-4 w-4" />
                        <span>{label}</span>
                        {currentStatus === status && (
                          <Check className="h-3 w-3 ml-auto text-blue-600" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="font-medium text-slate-900 text-sm leading-tight truncate">
                {title}
              </h4>
              {isDirty && (
                <span
                  className="h-1.5 w-1.5 rounded-full bg-blue-500 flex-shrink-0 animate-pulse"
                  title="Unsaved changes"
                />
              )}
            </div>
          </div>

          <button
            onClick={() => setExpanded(!expanded)}
            className="text-slate-400 hover:text-slate-600 flex-shrink-0"
          >
            <ChevronDown
              className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`}
            />
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-2">
          <div className="relative">
            <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input
              type="date"
              className="w-full text-xs rounded-md border border-slate-300 pl-8 pr-2 py-1.5 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={progress?.due_date ?? ""}
              onChange={(e) => onDueDateChange(id, e.target.value || null)}
              title="Due date"
            />
          </div>

          <div className="relative">
            <User className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <input
              className="w-full text-xs rounded-md border border-slate-300 pl-8 pr-2 py-1.5 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 truncate"
              placeholder="Owner"
              value={ownerDraft[id] ?? progress?.owner ?? ""}
              onChange={(e) => onOwnerChange(id, e.target.value)}
              title="Task owner"
            />
          </div>

          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs rounded-md border border-slate-300 px-2 py-1.5 hover:bg-slate-50 flex items-center gap-1.5 justify-center hover:border-blue-300 hover:text-blue-600 transition-colors"
          >
            <MessageSquare className="h-3.5 w-3.5" />
            <span>{expanded ? "Hide Notes" : "Add Notes"}</span>
          </button>
        </div>

        {expanded && (
          <div className="mt-3 pt-3 border-t border-slate-200 space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                Notes & Comments
              </label>
              <textarea
                rows={2}
                className="w-full text-sm rounded-lg border border-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
                placeholder="Add notes, comments, or instructions..."
                value={commentDraft[id] ?? progress?.comment ?? ""}
                onChange={(e) => onCommentChange(id, e.target.value)}
              />
            </div>

            {description && (
              <div>
                <div className="text-xs font-medium text-slate-500 mb-1.5">
                  Task Description
                </div>
                <div className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3 leading-relaxed">
                  {description}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default function UserRoadmapPage({
  mode = "project" as "project" | "other",
}) {
  const { projectId } = useParams();
  const pid = mode === "project" ? (projectId ?? null) : null;

  const {
    loading: roadmapLoading,
    error: roadmapError,
    chapters,
    stepsByChapter,
    progressByStep,
    setStatus,
    setDueDate,
    setOwner,
    setComment,
  } = useLinkedRoadmap(pid);

  const [activeChapterId, setActiveChapterId] = useState<string>("");
  const [projectData, setProjectData] = useState<any>(null);
  const [projectLoading, setProjectLoading] = useState(true);
  const [ownerDraft, setOwnerDraft] = useState<Record<string, string>>({});
  const [commentDraft, setCommentDraft] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  const [filter, setFilter] = useState<FilterState>({
    status: "all",
    showOwner: true,
    showDueDate: true,
    showComments: true,
  });

  const [showFilterPanel, setShowFilterPanel] = useState(false);

  const hasDrafts = useMemo(
    () =>
      Object.keys(ownerDraft).length > 0 ||
      Object.keys(commentDraft).length > 0,
    [ownerDraft, commentDraft],
  );

  useEffect(() => {
    if (!pid) {
      setProjectLoading(false);
      return;
    }
    setProjectLoading(true);
    getProject(pid)
      .then(setProjectData)
      .catch((e) => console.error("Failed to load project:", e))
      .finally(() => setProjectLoading(false));
  }, [pid]);

  useEffect(() => {
    if (!activeChapterId && chapters?.length) {
      setActiveChapterId(String(chapters[0].id));
    }
  }, [chapters, activeChapterId]);

  const chapterCounts = useCallback(
    (chId: string) => {
      const list: StepItem[] = stepsByChapter?.[chId] ?? [];
      const done = list.reduce(
        (acc, s: any) =>
          acc +
          ((progressByStep?.[String(s.id)]?.status ?? "not_started") === "done"
            ? 1
            : 0),
        0,
      );
      return { done, total: list.length };
    },
    [stepsByChapter, progressByStep],
  );

  const totals = useMemo(() => {
    const initialCounts: Record<Status | "total", number> = {
      total: 0,
      done: 0,
      in_progress: 0,
      stuck: 0,
      not_started: 0,
    };

    if (!chapters || !stepsByChapter) return initialCounts;

    return chapters.reduce((acc, ch: any) => {
      const list: StepItem[] = stepsByChapter[String(ch.id)] ?? [];
      for (const s of list) {
        const st = (progressByStep?.[String(s.id)]?.status ??
          "not_started") as Status;
        acc.total += 1;
        acc[st] += 1;
      }
      return acc;
    }, initialCounts);
  }, [chapters, stepsByChapter, progressByStep]);

  const filteredSteps = useMemo(() => {
    const steps = stepsByChapter?.[activeChapterId] ?? [];

    if (filter.status === "all") {
      return steps;
    }

    return steps.filter((s: any) => {
      const id = String(s.id);
      const currentStatus = (progressByStep?.[id]?.status ??
        "not_started") as Status;
      return currentStatus === filter.status;
    });
  }, [stepsByChapter, activeChapterId, progressByStep, filter.status]);

  const { totalRooms, totalCost, currency, projectName } = useMemo(() => {
    const kpis = projectData?.kpis;

    const totalRooms =
      kpis?.roomMix?.reduce((sum: number, r: any) => sum + (r.count || 0), 0) ??
      projectData?.roomTypes?.reduce(
        (sum: number, r: any) => sum + (r.rooms || 0),
        0,
      ) ??
      0;

    const totalCost = kpis?.investmentBudget?.grandTotal ?? 0;
    const currency = projectData?.currency ?? kpis?.currency ?? "EUR";
    const projectName = projectData?.name ?? kpis?.dealName ?? "";

    return { totalRooms, totalCost, currency, projectName };
  }, [projectData]);

  async function saveAllDrafts() {
    if (!hasDrafts) return;
    try {
      setSaving(true);

      const ownerPromises = Object.entries(ownerDraft).map(([id, val]) =>
        setOwner(id, val || null),
      );
      const commentPromises = Object.entries(commentDraft).map(([id, val]) =>
        setComment(id, val || null),
      );

      await Promise.all([...ownerPromises, ...commentPromises]);

      setOwnerDraft({});
      setCommentDraft({});
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 1800);
    } catch (e) {
      console.error("Save failed:", e);
    } finally {
      setSaving(false);
    }
  }

  const handleOwnerChange = (id: string, value: string) => {
    setOwnerDraft((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleCommentChange = (id: string, value: string) => {
    setCommentDraft((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleFilterStatusChange = (status: FilterStatus) => {
    setFilter((prev) => ({ ...prev, status }));
  };

  const handleClearFilters = () => {
    setFilter({
      status: "all",
      showOwner: true,
      showDueDate: true,
      showComments: true,
    });
  };

  const statusConfig = {
    all: { label: "All Tasks", color: "text-slate-700 bg-slate-100" },
    not_started: { label: "Not Started", color: "text-slate-500 bg-slate-100" },
    in_progress: { label: "In Progress", color: "text-blue-500 bg-blue-50" },
    done: { label: "Done", color: "text-emerald-500 bg-emerald-50" },
    stuck: { label: "Stuck", color: "text-amber-500 bg-amber-50" },
  };

  const loadingState = roadmapLoading || projectLoading;

  if (roadmapError)
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="rounded-lg bg-rose-50 border border-rose-200 p-6">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-6 w-6 text-rose-500" />
            <div>
              <h3 className="font-semibold text-rose-800">
                Error Loading Roadmap
              </h3>
              <p className="text-rose-700 mt-1">{roadmapError}</p>
            </div>
          </div>
        </div>
      </div>
    );

  if (loadingState) return <RoadmapSkeleton />;

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6">
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">
              Project Roadmap
            </h1>
            <p className="text-slate-600 mt-2">
              Track and manage your project milestones
            </p>
          </div>
        </div>

        {pid && (
          <div className="rounded-2xl bg-gradient-to-r from-blue-50 to-slate-50 border border-slate-200 p-6 mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              <div>
                <h2 className="text-xl font-bold text-slate-900 mb-2">
                  {projectName || "Untitled Project"}
                </h2>
                <div className="flex flex-wrap gap-4">
                  {totalRooms > 0 && (
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center">
                        <Target className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-slate-900">
                          {totalRooms}
                        </div>
                        <div className="text-sm text-slate-600">
                          Total Rooms
                        </div>
                      </div>
                    </div>
                  )}
                  {totalCost > 0 && (
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center">
                        <BarChart3 className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-slate-900">
                          {formatCurrency(totalCost, currency)}
                        </div>
                        <div className="text-sm text-slate-600">
                          Total Investment
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <ProgressRing value={totals.done} total={totals.total} />
                  <div className="text-sm font-medium text-slate-700 mt-2">
                    Overall Progress
                  </div>
                </div>
                <div className="h-12 w-px bg-slate-300 hidden lg:block" />
                <div className="grid grid-cols-2 gap-3 min-w-[200px]">
                  <div className="text-center">
                    <div className="text-xl font-bold text-slate-900">
                      {totals.done}
                    </div>
                    <div className="text-xs text-slate-600">Completed</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-slate-900">
                      {totals.in_progress}
                    </div>
                    <div className="text-xs text-slate-600">In Progress</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-slate-900">
                      {totals.stuck}
                    </div>
                    <div className="text-xs text-slate-600">Stuck</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-slate-900">
                      {totals.not_started}
                    </div>
                    <div className="text-xs text-slate-600">Not Started</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {hasDrafts && (
        <div className="sticky top-4 z-20 mb-6">
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100">
                  <AlertCircle className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <div className="font-medium text-blue-900">
                    You have unsaved changes
                  </div>
                  <div className="text-sm text-blue-700">
                    {Object.keys(ownerDraft).length +
                      Object.keys(commentDraft).length}{" "}
                    task(s) modified
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setOwnerDraft({});
                    setCommentDraft({});
                  }}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 transition-colors"
                >
                  Discard
                </button>
                <button
                  onClick={saveAllDrafts}
                  disabled={saving}
                  className="rounded-lg bg-primary-blue px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 transition-colors"
                  style={{ backgroundColor: "hsl(215 85% 45%)" }}
                >
                  {saving ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      Save All Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="sticky top-24">
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900">Chapters</h3>
                <span className="text-sm text-slate-500">
                  {chapters?.length ?? 0} total
                </span>
              </div>
              <div className="space-y-2 max-h-[calc(100vh-300px)] overflow-y-auto pr-2">
                {(chapters ?? []).map((c: any, i: number) => {
                  const id = String(c.id);
                  const { done, total } = chapterCounts(id);
                  return (
                    <ChapterCard
                      key={id}
                      chapter={c}
                      index={i}
                      isActive={id === activeChapterId}
                      onClick={() => setActiveChapterId(id)}
                      done={done}
                      total={total}
                    />
                  );
                })}
              </div>
            </div>

            <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">
              <h4 className="font-semibold text-slate-900 mb-3">
                Status Legend
              </h4>
              <div className="space-y-2">
                {(
                  ["not_started", "in_progress", "done", "stuck"] as Status[]
                ).map((status) => (
                  <div
                    key={status}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className={`h-3 w-3 rounded-full ${statusDot[status]}`}
                      />
                      <span className="text-sm text-slate-700 capitalize">
                        {status.replace("_", " ")}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-slate-900">
                      {totals[status]}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="lg:col-span-2">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">
                  {activeChapterId
                    ? chapters?.find(
                        (c: any) => String(c.id) === activeChapterId,
                      )?.title
                    : "Select a Chapter"}
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  {filter.status === "all"
                    ? `${stepsByChapter?.[activeChapterId]?.length ?? 0} tasks in this chapter`
                    : `${filteredSteps.length} of ${stepsByChapter?.[activeChapterId]?.length ?? 0} tasks match filter`}
                </p>
              </div>

              <div className="flex items-center gap-2">
                {filter.status !== "all" && (
                  <button
                    onClick={handleClearFilters}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    <span>Clear Filter</span>
                  </button>
                )}

                <div className="relative">
                  <button
                    onClick={() => setShowFilterPanel(!showFilterPanel)}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    <Filter className="h-4 w-4" />
                    Filter
                    {filter.status !== "all" && (
                      <span
                        className={`h-2 w-2 rounded-full ${statusConfig[filter.status].color.split(" ")[0]}`}
                      />
                    )}
                  </button>

                  {showFilterPanel && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setShowFilterPanel(false)}
                      />
                      <div className="absolute right-0 top-full mt-1 z-20 w-56 rounded-lg border border-slate-200 bg-white shadow-lg py-2">
                        <div className="px-3 py-2 border-b border-slate-100">
                          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                            Filter by Status
                          </div>
                        </div>

                        <div className="py-1">
                          {(
                            Object.entries(statusConfig) as [
                              FilterStatus,
                              any,
                            ][]
                          ).map(([status, config]) => (
                            <button
                              key={status}
                              onClick={() => {
                                handleFilterStatusChange(status);
                                setShowFilterPanel(false);
                              }}
                              className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-slate-50 text-left"
                            >
                              <div className="flex items-center gap-2">
                                <div
                                  className={`h-3 w-3 rounded-full ${statusDot[status as Status] || "bg-slate-300"}`}
                                />
                                <span>{config.label}</span>
                              </div>
                              {filter.status === status && (
                                <Check className="h-4 w-4 text-blue-600" />
                              )}
                            </button>
                          ))}
                        </div>

                        <div className="px-3 py-2 border-t border-slate-100 text-xs text-slate-500">
                          Showing:{" "}
                          {filter.status === "all"
                            ? "All tasks"
                            : statusConfig[filter.status].label}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {filteredSteps.length === 0 ? (
              <div className="text-center py-12">
                <div className="mx-auto max-w-xs">
                  <div className="rounded-full bg-slate-100 p-4 w-fit mx-auto">
                    <Filter className="h-8 w-8 text-slate-400" />
                  </div>
                  <h4 className="mt-4 text-lg font-semibold text-slate-900">
                    No tasks found
                  </h4>
                  <p className="mt-2 text-slate-600">
                    {filter.status === "all"
                      ? "This chapter doesn't contain any tasks."
                      : `No tasks with status "${statusConfig[filter.status].label}" found.`}
                  </p>
                  {filter.status !== "all" && (
                    <button
                      onClick={handleClearFilters}
                      className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                    >
                      Clear Filter
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredSteps.map((s: any) => {
                  const id = String(s.id);
                  const prog = progressByStep?.[id] ?? {};
                  return (
                    <TaskCard
                      key={id}
                      task={s}
                      progress={prog}
                      ownerDraft={ownerDraft}
                      commentDraft={commentDraft}
                      onStatusChange={setStatus}
                      onDueDateChange={setDueDate}
                      onOwnerChange={handleOwnerChange}
                      onCommentChange={handleCommentChange}
                    />
                  );
                })}
              </div>
            )}
          </div>

          {justSaved && (
            <div className="fixed bottom-4 right-4 z-50">
              <div className="animate-in slide-in-from-bottom-4 flex items-center gap-3 rounded-lg bg-emerald-50 px-4 py-3 shadow-lg border border-emerald-200">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100">
                  <Check className="h-4 w-4 text-emerald-600" />
                </div>
                <div>
                  <div className="font-medium text-emerald-900">
                    Changes saved successfully
                  </div>
                  <div className="text-sm text-emerald-700">
                    All modifications have been saved
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
